import React, { useState, useRef, useEffect } from 'react';
import './ChatBot.css';

const AI_URL = process.env.REACT_APP_AI_URL || 'http://localhost:3001';

function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;

    const userMsg = { role: 'user', content: text };
    const placeholderMsg = { role: 'assistant', content: '' };
    setHistory((prev) => [...prev, userMsg, placeholderMsg]);
    setInput('');
    setStreaming(true);

    try {
      const res = await fetch(`${AI_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, history }),
      });

      if (!res.ok) throw new Error('AI service unavailable');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') break;
          try {
            const { token, error } = JSON.parse(payload);
            if (error) throw new Error(error);
            if (token) {
              setHistory((prev) => {
                const next = [...prev];
                next[next.length - 1] = {
                  role: 'assistant',
                  content: next[next.length - 1].content + token,
                };
                return next;
              });
            }
          } catch {
            // malformed chunk — skip
          }
        }
      }
    } catch (err) {
      setHistory((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: 'assistant',
          content: `Sorry, I'm having trouble connecting right now. Please create a support ticket and we'll get back to you.`,
        };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      <button
        className="chatbot-fab"
        onClick={() => setIsOpen((o) => !o)}
        aria-label="Open AI chat assistant"
      >
        <span className="chatbot-fab-icon">🤖</span>
        <span className="chatbot-fab-label">Chat with our AI</span>
      </button>

      {isOpen && (
        <div className="chatbot-modal" role="dialog" aria-label="AI Support Chat">
          <div className="chatbot-header">
            <span>🤖 Yoru AI Support</span>
            <button
              className="chatbot-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              ✕
            </button>
          </div>

          <div className="chatbot-messages">
            {history.length === 0 && (
              <div className="chatbot-welcome">
                Hi! Ask me anything about your Yoru order — shipping, status, or general questions.
              </div>
            )}
            {history.map((msg, i) => (
              <div
                key={i}
                className={`chatbot-bubble chatbot-bubble--${msg.role}`}
              >
                {msg.content || (msg.role === 'assistant' && streaming ? <span className="chatbot-cursor" /> : null)}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="chatbot-input-row">
            <textarea
              ref={inputRef}
              className="chatbot-input"
              placeholder="Type your question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={streaming}
              rows={2}
            />
            <button
              className="chatbot-send"
              onClick={sendMessage}
              disabled={streaming || !input.trim()}
              aria-label="Send message"
            >
              {streaming ? '…' : '↑'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ChatBot;
